'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { motion, useTransform, type MotionValue } from 'motion/react';
import { customRichTextTags } from '@/utils/i18n';
import { PatientCard } from './reanalysis-patient-card';
import {
  CONCLUSION_SETTLED,
  SCENE_START,
  SUMMARY_TYPING,
} from './reanalysis-timeline';

// 1920×1080 고정 캔버스를 축소해 그리므로 뷰포트별 줄바꿈 분기가 없다 — 항상 줄바꿈하는 <br>
const richTags = { br: customRichTextTags.solidBr };
// 뒷면 패널 크기: 요약 진입 → 타이핑 시작까지 확장, 마지막 장면에서 화면 전체로
const PANEL_KEYFRAMES = [
  SCENE_START.summary,
  SUMMARY_TYPING.start,
  SCENE_START.conclusion,
  CONCLUSION_SETTLED,
];

export default function FeaturedPatient({
  progress,
}: {
  progress: MotionValue<number>;
}) {
  const t = useTranslations('Main.reanalysis');
  const y = useTransform(
    progress,
    [SCENE_START.patients, 0.25, SCENE_START.focus, 0.4],
    [1381, 301, 301, 0],
  );
  const scale = useTransform(progress, [SCENE_START.focus, 0.4], [0.625, 1]);
  const opacity = useTransform(progress, [SCENE_START.patients, 0.2], [0, 1]);
  const rotateY = useTransform(progress, [0.44, 0.52], [0, 180]);
  const pendingOpacity = useTransform(
    progress,
    [0.59, SCENE_START.diagnosed],
    [1, 0],
  );
  const diagnosisOpacity = useTransform(
    progress,
    [SCENE_START.diagnosed, 0.615, 0.68, SCENE_START.summary],
    [0, 1, 1, 0],
  );
  const checkScale = useTransform(
    progress,
    [SCENE_START.diagnosed, 0.638, 0.666],
    [0, 1.25, 1],
  );
  const panelWidth = useTransform(
    progress,
    PANEL_KEYFRAMES,
    [480, 1360, 1360, 2300],
  );
  const panelHeight = useTransform(
    progress,
    PANEL_KEYFRAMES,
    [672, 952, 952, 1300],
  );
  const panelRadius = useTransform(
    progress,
    [SCENE_START.summary, SUMMARY_TYPING.start],
    [32, 40],
  );
  const spinnerRotation = useTransform(
    progress,
    [SCENE_START.analyzing, SCENE_START.diagnosed],
    [0, 720],
  );
  return (
    <div
      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 [perspective:1600px]"
      aria-hidden="true"
    >
      <motion.div
        className="relative [transform-style:preserve-3d]"
        style={{
          y,
          scale,
          opacity,
          rotateY,
          width: panelWidth,
          height: panelHeight,
        }}
      >
        <div className="absolute inset-0 [backface-visibility:hidden]">
          <PatientCard patient="cafe-au-lait" days={325} featured />
        </div>
        <motion.div
          className="absolute inset-0 grid place-items-center bg-[#0d0e10] p-[51.2px] [backface-visibility:hidden] [transform:rotateY(180deg)]"
          style={{ borderRadius: panelRadius }}
        >
          <motion.div
            className="col-start-1 row-start-1 flex flex-col items-center gap-4"
            style={{ opacity: pendingOpacity }}
          >
            <motion.div style={{ rotate: spinnerRotation }}>
              <Image
                src="/images/main/reanalysis/spinner.webp"
                alt=""
                width={39}
                height={39}
                className="h-[38.4px] w-[38.4px]"
              />
            </motion.div>
            <p className="bg-[linear-gradient(103deg,#fff_33.544%,rgba(212,222,238,0.31)_101.14%)] bg-clip-text text-[25.6px] font-medium leading-[38.4px] text-transparent">
              {t('pending', { days: 325 })}
            </p>
          </motion.div>
          <motion.div
            className="col-start-1 row-start-1 flex flex-col items-center gap-4"
            style={{ opacity: diagnosisOpacity }}
          >
            <motion.div style={{ scale: checkScale }}>
              <Image
                src="/images/main/icons/checkmark-48.svg"
                alt=""
                width={48}
                height={48}
                className="h-[38.4px] w-[38.4px]"
              />
            </motion.div>
            <p className="whitespace-nowrap text-center text-[25.6px] font-medium leading-[38.4px] text-[#d4deee]">
              {t.rich('diagnosis', {
                ...richTags,
                strong: chunks => (
                  <strong className="bg-gradient-to-b from-[#91b2ff] to-[#b6f8ff] bg-clip-text text-transparent">
                    {chunks}
                  </strong>
                ),
              })}
            </p>
          </motion.div>
        </motion.div>
      </motion.div>
    </div>
  );
}
